import React, { Component } from 'react';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import PropTypes from 'prop-types';
import Validation from '../../../shared/Validation';
import Header from '../../../shared/Header/Header';
import Button from '../../../shared/Button/Button';
import './SingleInput.scss';
import '../../../styles/cidMobile.scss';

import headerImage from '../Icons/phone.svg';
import Logout from '../../../shared/Logout/Logout';
import helpIcon from '../Icons/help.svg';
import crossIcon from '../Icons/cross.svg';
import Popover from '../../../shared/Popover/Popover.js';
import imprivataIcon from '../Icons/imprivata.svg';
import editIcon from '../Icons/Edit.svg';
import FloatingError from '../../../shared/FloatingError/FloatingError';
import FooterMobile from '../../../shared/FooterMobile/FooterMobile';

const numberMask = createNumberMask({
  prefix: '',
  includeThousandsSeparator: false,
  allowLeadingZeroes: true,
});

/**
 * Single input component can be used in multiple variations just by editing
 * props. Examples include:
 * * [Imprivata ID (time-out/manual) prompt](#imprivataId)
 * * [Password prompt (not applicable in the instance of Microsoft AD FS)](#password)
 * * [SMS prompt](#sms)
 * * [OTP prompt](#otp)
 * * [PIN prompt](#pin)
 * * [Temporary code prompt](#tempcode)
 * @class SingleInput
 * @extends {Component}
 */
class SingleInput extends Component {
  static propTypes = {
    /** Mask to apply to the input box. Set to false to remove input box masking. */
    inputMask: PropTypes.oneOfType([
      PropTypes.func, PropTypes.arrayOf(PropTypes.string), PropTypes.bool,
    ]),
    /** Header image */
    headerImage: PropTypes.string,
    /** Header title. Entering an __empty string__ will __hide__ the title.  */
    headerTitle: PropTypes.string,
    /** Header description. Entering an __empty string__ will __hide__ the description. */
    headerDescription: PropTypes.object,
    /** Decides whether to show the "Remember this device" text*/
    rememberText: PropTypes.string,
    /** Link for SSDM*/
    enrollDeviceLink: PropTypes.func,
    /** Link to the manage devices page*/
    manageDeviceLink: PropTypes.func,
    /** Placeholder text for the input field. */
    placeholderText: PropTypes.string,
    /** Type of input control */
    inputType: PropTypes.oneOf(['password', 'text',]),
    /** Function executed when input field change. */
    onInputChange: PropTypes.func,
    /** Function to execute when form is submitted. */
    onSubmit: PropTypes.func,
    /** Function executed when retry link is clicked. */
    onRetryClick: PropTypes.func,
    /** Text displayed on the retry link. Entering an __empty string__ will
     __hide__ the link. */
    retryText: PropTypes.string,
    /** link to display more options */
    moreOptionsLink: PropTypes.oneOfType([PropTypes.string, PropTypes.object,]),
    /** Text displayed on the submit button. */
    submitText: PropTypes.string,
    /** Is something loading? */
    isLoading: PropTypes.bool,
    /** Validation constraints object for validate.js
     (https://validatejs.org/#constraints) */
    validation: PropTypes.object,
    /** Errors to display inside of component from a parent component */
    displayErrors: PropTypes.arrayOf(PropTypes.string),
    /** Maximum length of value in input or blank for no maximum */
    maxLength: PropTypes.string,
    /** Whether the browser should attempt to autoComplete the field */
    autoComplete: PropTypes.bool,
    /** Whether the field should automatically gain focus */
    autoFocus: PropTypes.bool,
    /** Value of the input box */
    tokenCode: PropTypes.string,
    /** The component to be used for logout */
    logoutComponent: PropTypes.object,
    /** Text to be included in the information overlay*/
    infoPopout: PropTypes.string,
    /** The DOM tree/component to be used for the footer */
    footer: PropTypes.object,
    /** only set cidMobile to true for the cidMobile layout **/
    cidMobile: PropTypes.bool,
    /** Use Parkland custom UX */
    useCustomUX: PropTypes.bool,
    /** Username to be displayed */
    username: PropTypes.string,
    /** Allow editing of the username */
    allowEditUsername: PropTypes.bool,
    /** Function to navigate to the edit  username page */
    resetUsername: PropTypes.func,
  };

  static defaultProps = {
    inputMask: numberMask,
    isLoading: false,
    headerImage: headerImage,
    headerTitle: 'Imprivata ID',
    headerDescription: null,
    inputType: 'password',
    placeholderText: 'Token code',
    retryText: 'Retry',
    moreOptionsLink: null,
    submitText: 'Confirm your identity',
    validation: { tokenCode: { presence: true, }, },
    displayErrors: [],
    autoComplete: false,
    autoFocus: false,
    maxLength: '',
    onInputChange: () => void 0,
    onSubmit: () => void 0,
    tokenCode: '',
    logoutComponent: <Logout />,
    cidMobile: false,
    // Parkland custom UX
    useCustomUX: false,
  };

  constructor(props) {
    super(props);
    this.validation = new Validation(props.validation);
    this.state = {
      errors: '',
      showErrors: false,
      tokenCode: props.tokenCode,
      disableSubmit: true,
    };
  };

  componentDidUpdate = (prevProps) => {
    if (this.props.tokenCode !== prevProps.tokenCode) {
      this.setState({ tokenCode: this.props.tokenCode, });
    }
  };

  errorHelp = fieldName => {
    const errors = this.validation.errorMessages();
    if (errors && this.state.showErrors) {
      const message = errors[fieldName];
      return <span className='help-block'>{message}</span>;
    }
    return '';
  };

  changeInput = event => {
    const inputValues = { tokenCode: event.target.value, };
    this.validation.updateValues(inputValues);
    inputValues.disableSubmit = this.validation.hasOnlyRequiredErrors();
    inputValues.errors = this.validation.errorMessages();
    inputValues.showErrors = false;
    this.setState(inputValues);
    this.props.onInputChange(inputValues);
  };
  retrySend = event => {
    event.preventDefault();
    this.props.onRetryClick(event);
  };
  submitForm = event => {
    event.preventDefault();
    if (this.state.errors) {
      this.setState({ showErrors: true, });
    }
    else {
      this.setState({ showErrors: false, });
      this.props.onSubmit(this.state);
      this.setState({ tokenCode: '', });
    }
  };
  navigateToUsernamePage = event => {
    event.preventDefault();
    this.props.resetUsername();
  };
  render() {
    const rb = require('react-bootstrap');

    return (
      <div className={this.props.cidMobile ? 'cidMobile' : 'CIDRA'}>
        {this.props.logoutComponent}
        {this.props.useCustomUX ?
          (<Header />)
          :
          (<div className='Header-Box'>
            <img src={this.props.headerImage} className='Modality-Icon' alt='lock' />
            <div className={'Header-Text'}>
              <span>{this.props.headerTitle}</span>
            </div>
            <div className={this.props.cidMobile ? 'Header-Description' : 'Header-Description-hasIcon'}>
              <span>{this.props.headerDescription}</span>
            </div>
          </div>)
        }

        {this.props.enrollDeviceLink && (
          <div>
            <div className='Enroll-New-Device'>
              {this.props.manageDeviceLink
                ? <span className='Manage-Devices-Text' onClick={this.props.manageDeviceLink}>Manage devices</span>
                : null
              }
              <span className='Enroll-New-Device-Text' onClick={this.props.enrollDeviceLink}>Enroll new device</span>
            </div>
            <div className='Enroll-New-Device-Circle btn' onClick={this.props.enrollDeviceLink} />
            <img className='Enroll-New-Device-Cross' src={crossIcon} onClick={this.props.enrollDeviceLink} alt='cross' />
          </div>
        )}
        <div className='login-container'>
          <form id='SingleInput_form' className='form' data-testid='single-input-form' onSubmit={this.submitForm}>
            {this.props.displayErrors.length > 0 ?
              (
                <div>
                  <FloatingError
                    messages={this.props.displayErrors}
                    showDuration={0} />
                </div>
              )
              : null
            }
            <div className='form-group no-margin-bottom'>
              {this.props.children}
            </div>
            { this.props.username ? (
              this.props.allowEditUsername ? (
                // eslint-disable-next-line
                <a id='SingleInput_change_username_link'
                  className='username-container'
                  href='' onClick={this.navigateToUsernamePage}>
                  <div id='SingleInput_username' className='username'>
                    {this.props.username}
                  </div>
                  &nbsp;
                  <img src={editIcon} alt='logo'/>
                </a>
              ) : (
                <div className='username-container'>
                  <div id='SingleInput_username' className='username'>
                    {this.props.username}
                  </div>
                </div>
              )
            ) : null
            }
            <div className='form-group CIDRA-Field'>
              <MaskedInput
                mask={this.props.inputMask}
                guide={false}
                autoFocus={this.props.autoFocus}
                type={this.props.inputType}
                id='SingleInput_token_code_input'
                className='form-control'
                placeholder={this.props.placeholderText}
                name='tokenCode'
                onChange={this.changeInput}
                maxLength={this.props.maxLength}
                autoComplete={this.props.autoComplete ? 'on' : 'off'}
                value={this.state.tokenCode}
              />
            </div>
            {this.errorHelp('tokenCode')}
            <div className='form-group CIDRA-Field'>
              {this.props.useCustomUX ?
                (
                  <Button
                    id='SingleInput_submit_button'
                    disabled={this.state.disableSubmit}
                    onClick={this.submitForm}
                    submitText='Sign In'
                  />
                )
                :
                (<button
                  id='SingleInput_submit_button'
                  className='btn btn-primary btn-block'
                  type='submit'
                  disabled={this.state.disableSubmit}
                  onClick={this.submitForm}
                >
                  {this.props.submitText}
                </button>
                )
              }
            </div>
          </form>
          {(this.props.retryText) && (
            <div className='text-center Font-Size-12'>
              {this.props.retryText && (
                <div>
                  {this.props.retryText}
                  {' '}
                  {
                    // eslint-disable-next-line
                  }<a
                    href=''
                    id='SingleInput_retry_link'
                    className='Font-Size-12'
                    onClick={this.retrySend}
                  >
                    Retry
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
        {this.props.footer}
        <div>
          {(this.props.rememberText) && (
            <div className='text-center Remember-Text-Manual'>
              <input type='checkbox' name='rememberBox' value='remember' />
              &emsp;
              {this.props.rememberText}
              <Popover
                body={<div className='popover-text'>{this.props.infoPopout}</div>}
                trigger={<rb.Button bsstyle='link'>
                  <img className='Help-Icon' src={helpIcon} alt='help' />
                </rb.Button>}
                triggerType={['hover', 'focus',]}
              />
            </div>
          )}
          {(this.props.moreOptionsLink) && (
            <div className='text-center More-Options'>
              {this.props.moreOptionsLink}
            </div>
          )}
        </div>
        {this.props.cidMobile ? (
          <FooterMobile />
        )
          :
          (
            <div>
              <hr className='Footer-hr' />
              <img className='Footer' src={imprivataIcon} alt='logo' />
            </div>
          )
        }
      </div>
    );
  }
}

export default SingleInput;
